import { useEffect } from 'react';
import { compose, find, path, prop, propEq } from 'ramda';
import { branch, renderComponent, renderNothing, withProps } from 'recompose';
import { useNavigate } from 'js/hooks/navigate';
import { componentService } from 'js/services/component.service';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { withPortlet, usePortletConfig } from 'js/components/system/Portlet';

import { isCountdownAvailable } from '../helpers';
import { DISPLAY_PLAYER, LEADER_BOARD_DETAILS } from './constants';
import { withEmptyState } from '../common/EmptyState';
import withLeaderBoardDetails from './withDetails';
import Details from './Details';
import Skeleton from './Skeleton';
import * as hocs from './hocs';

export const NavigateHome = () => {
    const navigateWithReplace = useNavigate({ replace: true });
    const rootPage = useAppSelector(prop('rootPage'));

    useEffect(() => {
        navigateWithReplace(rootPage);
    }, [rootPage, navigateWithReplace]);

    return null;
};

export const onlyForPlayers = branch(
    () => useUserSelector(prop('role')) === 'admin',
    renderNothing
);

export const withTopCompetitorsCount = withProps(
    () => {
        const { topCompetitorsCount } = usePortletConfig();

        if (topCompetitorsCount) {
            return {
                topCompetitorsCount: parseInt(topCompetitorsCount)
            };
        }
    }
);

export const withNetworkId = withProps(
    () => {
        const { displayPlayer, networkId, networkType } = useAppSelector(prop('leaderboard'));
        const playerNetworkData = useUserSelector(path(['userDetails', 'playerNetworkData']));
        const findNetworkData = data => data && find(propEq(networkType), data);

        switch (displayPlayer) {
            case DISPLAY_PLAYER.nicknameByNetworkType: {
                const networkData = findNetworkData(playerNetworkData);

                return networkData && {
                    networkId: networkData.networkId
                };
            }
            case DISPLAY_PLAYER.nicknameByNetworkId:
                return {
                    networkId
                };
            default:
                break;
        }
    }
);

export const RaceDetails = compose(
    withPortlet({ forLoggedIn: true }),
    onlyForPlayers,
    hocs.withLeaderBoardId,
    branch(
        ({ leaderboardId }) => !leaderboardId,
        renderComponent(NavigateHome)
    ),
    withTopCompetitorsCount,
    withNetworkId,
    withLeaderBoardDetails,
    branch(
        ({ details, error }) => !details && !error,
        renderComponent(Skeleton)
    ),
    withEmptyState,
    withProps(
        ({ details }) => {
            const {
                leaderboardRules = [],
                topPrizes = [],
                commonPrize,
                status
            } = details;

            const {
                displayPrizes,
                displayCompetitorsTable: displayTable,
                competitorsTablePositioning,
                displayCountdown,
                countdownType
            } = usePortletConfig();

            return {
                competitorsTablePositioning,
                displayTable,
                displayCountdown: isCountdownAvailable(status, displayCountdown, countdownType),
                displayGames: Boolean(leaderboardRules.length),
                displayPrizes: Boolean(topPrizes.length || commonPrize) && displayPrizes
            };
        }
    )
)(Details);

componentService.registerPageComponents({ [LEADER_BOARD_DETAILS]: RaceDetails });
